
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarSeparator,
} from "@/components/ui/sidebar";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { SidebarHeader } from "./sidebar/SidebarHeader";
import { SidebarMenu } from "./sidebar/SidebarMenu";
import { SidebarFooterContent } from "./sidebar/SidebarFooterContent";
import { useSession } from "@/hooks/use-session";
import { useClientInfo } from "@/hooks/use-client-info";

export function AppSidebar() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { session, isInitialized, clearSessionAndRedirect } = useSession();
  const { clientLogo, clientName } = useClientInfo(session);

  const handleLogout = async () => {
    try {
      // First clear the local state
      clearSessionAndRedirect();
      
      // Then attempt to sign out from Supabase
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('Logout error:', error);
        // Even if there's an error, we've already cleared the local state
        // and redirected the user, so we just show a different message
        toast({
          title: "Logged out",
          description: "You have been logged out successfully.",
        });
        return;
      }

      toast({
        title: "Success",
        description: "You have been logged out.",
      });
    } catch (error) {
      console.error('Logout error:', error);
      // Even if there's an error, we've already cleared the local state
      toast({
        title: "Logged out",
        description: "You have been logged out successfully.",
      });
    }
  };

  const onMenuClick = async (path: string) => {
    navigate(path);
  };

  // Don't render anything until we've initialized the session
  if (!isInitialized) {
    return null;
  }

  // After initialization, only render if there's a valid session
  if (!session) {
    return null;
  }

  return (
    <Sidebar>
      <SidebarContent>
        <SidebarHeader logoUrl={clientLogo} clientName={clientName} />
        <SidebarMenu onMenuClick={onMenuClick} />
      </SidebarContent>
      <SidebarFooter>
        <SidebarSeparator />
        <SidebarFooterContent 
          onLogout={handleLogout}
        />
      </SidebarFooter>
    </Sidebar>
  );
}
